import { SmartSearchType } from './tables/Global'

const MAX_POINT = 3
const OPTIMAL_PERCENTAGE_CORRECT = 0.7

type Likeness = number
type CorrectionPositioning = (number | null)[]

type DictionaryMapData<T = number> = {
  similar: {
    likeness: Likeness
    word: T
  }[] // Words that are one character off
  fullTerm: T[] // Words that are parent to the current word
}

export type FullDictionaryData = {
  likeness: Likeness
  word: string
} & DictionaryMapData<string>

const SEPARATORS = [' ', '@', '-']
const SEPARATORS_REGEX = new RegExp(`[${SEPARATORS.map((s) => `\\${s}`).join('')}]`, 'g')

export class AutoCompleteManager {
  // eg. customer.name, customer.email
  column: Record<string, AutoComplete> = {}
  columns: string[] = []

  addColumn(columnName: string) {
    if (this.column[columnName]) return

    this.column[columnName] = new AutoComplete()
    this.columns.push(columnName)
  }

  addNameIfInitiated(columnName: string, term: string) {
    if (!this.column[columnName] || term === '') return

    this.column[columnName].addName(term)
  }

  getColumn(columnName: string): AutoComplete | undefined {
    return this.column[columnName]
  }

  static formatKey(tableName: string, columnName?: string) {
    return columnName ? tableName + '.' + columnName : tableName
  }
}

export class AutoComplete {
  dictionary: string[] = []
  dictionaryMap: Record<string, DictionaryMapData> = {}

  fullNameDictionary: string[] = []

  constructor() {}

  getCorrectedSearch(query: string, searchType?: SmartSearchType): string {
    switch (searchType) {
      case 'Exact':
        return query
      case 'Lax':
        return this.getCorrectedSearchLax(query)

      case 'Strict':
      default:
        return this.getCorrectedSearchStrict(query)
    }
  }

  getCorrectedSearchStrict(query: string): string {
    const words = query.toLowerCase().split(/[ ]/g)
    const result: string[] = []

    for (let i = 0; i < words.length; i++) {
      const word = words[i]

      const similarWord = this.findMostSimilarWord(word, 0.9)

      if (!similarWord.word) {
        result.push(word)
        continue
      }

      const overlappedSearch = WordComparisonAlgorithm.overlapSearch(
        similarWord.word,
        similarWord.correctionPositioning
      )
      if (overlappedSearch.includes('%')) result.push(word)
      else result.push(overlappedSearch)
    }

    const correctedSearch = result.join('%')

    let numberOfNonWildcardChars = 0
    for (let i = 0; i < correctedSearch.length; i++) {
      if (!/[%_]/.test(correctedSearch.charAt(i))) numberOfNonWildcardChars++
    }

    if (numberOfNonWildcardChars < 3) return query

    return correctedSearch.replace(/[kc]/g, '_')
  }

  getCorrectedSearchLax(query: string): string {
    const words = query.toLowerCase().split(SEPARATORS_REGEX)
    const result: string[] = []

    for (let i = 0; i < words.length; i++) {
      const word = words[i]

      const similarWord = this.findMostSimilarWord(word)

      if (!similarWord.word) {
        result.push(word)
        continue
      }

      result.push(
        WordComparisonAlgorithm.overlapSearch(similarWord.word, similarWord.correctionPositioning)
      )
    }

    const correctedSearch = result.join('%')

    let numberOfNonWildcardChars = 0
    for (let i = 0; i < correctedSearch.length; i++) {
      if (!/[%_]/.test(correctedSearch.charAt(i))) numberOfNonWildcardChars++
    }

    if (numberOfNonWildcardChars < 3) return query

    // Remove commonly misstyped characters such as 'c' and 'k', since they are often interchanged
    return correctedSearch.replace(/[kc]/g, '_')
  }

  getSuggestions(query: string, maxResults: number = 10): string[] {
    const words = query.toLowerCase().split(SEPARATORS_REGEX)

    // Saves a little time by not reallocating the array when inserting the results
    const results: [Likeness, DictionaryMapData, string][] = new Array(this.dictionary.length)

    for (let i = 0; i < words.length; i++) {
      const word = words[i]

      // If word exists in the dictionary, add it to the results
      if (word in this.dictionaryMap) {
        results[i] = [word.length * MAX_POINT, this.dictionaryMap[word], word]
        continue
      }

      const dictionaryLength = this.dictionary.length
      for (let j = 0; j < dictionaryLength; j++) {
        const [likeness] = WordComparisonAlgorithm.calculateLikeness_Without_Positioning(
          word,
          this.dictionary[j]
        )

        const percentageMatch = WordComparisonAlgorithm.getPercentageMatch(word, likeness)

        if (percentageMatch > OPTIMAL_PERCENTAGE_CORRECT)
          results.push([
            percentageMatch,
            this.dictionaryMap[this.dictionary[j]],
            this.dictionary[j],
          ])
      }
    }

    const uniqueResults: {
      [key: string]: {
        fullTerm: string
        likeness: Likeness
      }
    } = {}

    results
      .sort((a, b) => {
        const likeness = b[0] - a[0]
        return likeness
      })
      .slice(0, maxResults)
      .forEach((a) => {
        // Add the similar words to the unique results
        a[1].similar.forEach((s) => {
          this.dictionaryMap[this.dictionary[s.word]].fullTerm.forEach((s) => {
            const fullTerm = this.fullNameDictionary[s]
            if (!(fullTerm in uniqueResults)) {
              const [likeness] = WordComparisonAlgorithm.calculateLikeness_Without_Positioning(
                query,
                fullTerm.toLowerCase()
              )

              if (
                WordComparisonAlgorithm.getPercentageMatch(query, likeness) >
                OPTIMAL_PERCENTAGE_CORRECT
              )
                uniqueResults[fullTerm] = {
                  fullTerm: fullTerm,
                  likeness,
                }
            }
          })
        })

        // Add the full term to the unique results
        a[1].fullTerm.forEach((s) => {
          const fullTerm = this.fullNameDictionary[s]
          if (!(fullTerm in uniqueResults)) {
            const [likeness] = WordComparisonAlgorithm.calculateLikeness_With_Positioning(
              query,
              fullTerm.toLowerCase()
            )

            if (
              WordComparisonAlgorithm.getPercentageMatch(query, likeness) >
              OPTIMAL_PERCENTAGE_CORRECT
            )
              uniqueResults[fullTerm] = {
                fullTerm: fullTerm,
                likeness,
              }
          }
        })
      })

    // Sort the results by likeness and length
    const sortedResults = Object.values(uniqueResults).sort((a, b) => {
      const likeness = b.likeness - a.likeness || a.fullTerm.length - b.fullTerm.length
      return likeness
    })
    return sortedResults.slice(0, maxResults).map((s) => s.fullTerm)
  }

  findMostSimilarWord(
    word: string,
    strictness: number = OPTIMAL_PERCENTAGE_CORRECT
  ): {
    word: string | null
    correctionPositioning: CorrectionPositioning
  } {
    const highestLikeness: {
      likeness: number
      word: string | null
      correctionPositioning: CorrectionPositioning
    } = {
      likeness: 0,
      word: null,
      correctionPositioning: [],
    }
    const dictionaryLength = this.dictionary.length
    for (let j = 0; j < dictionaryLength; j++) {
      if (word === this.dictionary[j]) continue
      const [likeness, correctionPositioning] =
        WordComparisonAlgorithm.calculateLikeness_With_Positioning(word, this.dictionary[j])

      const percentageMatch = WordComparisonAlgorithm.getPercentageMatch(word, likeness)

      if (percentageMatch > strictness && percentageMatch > highestLikeness.likeness) {
        highestLikeness.likeness = percentageMatch
        highestLikeness.word = this.dictionary[j]
        highestLikeness.correctionPositioning = correctionPositioning
      }
    }

    return {
      word: highestLikeness.word,
      correctionPositioning: highestLikeness.correctionPositioning,
    }
  }

  addName(term: string) {
    // could be made O(1) with a set, but with such a small number its probably not worth it
    if (this.fullNameDictionary.includes(term)) return

    // Add the full name to the dictionary
    this.fullNameDictionary.push(term)

    // Handle the individual words of the full name
    const words = term.split(SEPARATORS_REGEX)
    for (const word of words) {
      // Skip words that are too short
      if (word.length <= 2) continue

      this.addWord(word, this.fullNameDictionary.length - 1)
    }
  }

  // This function adds a word to the dictionary and links it to other words
  addWord(word: string, fullTermIndex: number) {
    word = word.toLowerCase()

    if (word in this.dictionaryMap) {
      if (!this.dictionaryMap[word].fullTerm.includes(fullTermIndex))
        this.dictionaryMap[word].fullTerm.push(fullTermIndex)

      return
    }

    this.dictionary.push(word)
    this.dictionaryMap[word] = {
      similar: [],
      fullTerm: [fullTermIndex],
    }

    // Very small optimization, probably not worth it but but...
    const dictionaryLength = this.dictionary.length

    for (let i = 0; i < dictionaryLength; i++) {
      if (word === this.dictionary[i]) continue

      const [likeness] = WordComparisonAlgorithm.calculateLikeness_Without_Positioning(
        word,
        this.dictionary[i]
      )

      // should be done better probably, but for now it works, and not worth the time
      const [likenessFlipped] = WordComparisonAlgorithm.calculateLikeness_Without_Positioning(
        this.dictionary[i],
        word
      )

      const percentageMatch = WordComparisonAlgorithm.getPercentageMatch(word, likeness)
      if (percentageMatch > OPTIMAL_PERCENTAGE_CORRECT) {
        // Link word to the similar word
        this.dictionaryMap[word].similar.push({
          likeness: percentageMatch,
          word: i,
        })
      }

      const percentageMatchFlipped = WordComparisonAlgorithm.getPercentageMatch(
        this.dictionary[i],
        likenessFlipped
      )
      if (percentageMatchFlipped > OPTIMAL_PERCENTAGE_CORRECT) {
        // Link word to the similar word
        this.dictionaryMap[this.dictionary[i]].similar.push({
          likeness: percentageMatchFlipped,
          word: dictionaryLength - 1,
        })
      }
    }
  }
}

export namespace WordComparisonAlgorithm {
  /*
   * This function calculates the likeness of two words
   * It does so by comparing the characters of the words and giving points to the characters that match
   * The function also returns an array of the correct positioning of the characters of the query in the word
   * It checks for the following:
   *    1. If the characters match
   *    2. If the characters are one off
   *    3. If the query-character appears later in the word
   *     - eg. if you skip searching for middle names
   *
   * Example:
   *    Scoring works as follows:
   *              3223333133333
   *     query:   Jaokim Rangel
   *      word:   Joakim Lund Rangel
   *
   * NOTE:  The function cares about the positioning of the input,
   *        meaning that 'query' and 'wordInDictionary' should be passed in relative,
   *        switching the position of the arguments will give different results
   *        This is because the function compares the query only two letters at a time
   *        and stops when it cant find one of those two letters. Any other way wil result
   *        in worse results since it need to take into account the length so that it doesn't
   *        delete too much of the word. Can probably be done better but for now it works :)
   *
   * @param query The query to compare
   * @param wordInDictionary The word to compare
   * @returns [Likeness, CorrectionPositioning]
   * */
  export function calculateLikeness_With_Positioning(
    query: string,
    word: string
  ): [Likeness, CorrectionPositioning] {
    let queryPointer = 0
    let wordPointer = 0 //wordInDictionary.indexOf(query.charAt(0))
    let likeness = 0
    let breakInWord = false

    // an array of the correct positioning of the characters of the query in the word
    const correctionPositioning = []

    while (wordPointer < word.length && queryPointer < query.length) {
      // checks if the characters match
      if (WordComparisonAlgorithm.charsMatch(query, queryPointer, word, wordPointer)) {
        correctionPositioning.push(wordPointer)
        likeness += breakInWord ? 1 : MAX_POINT
        queryPointer++
        breakInWord = false
      }
      // checks if the characters are one off, in front
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer, word, wordPointer + 1)
      ) {
        correctionPositioning.push(wordPointer + 1)
        likeness += 2
        queryPointer++
      }
      // checks if the characters are one off, behind
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer, word, wordPointer - 1)
      ) {
        correctionPositioning.push(wordPointer - 1)
        likeness += 2
        queryPointer++
      }
      // checks if the query is one off, in front
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer + 1, word, wordPointer)
      ) {
        correctionPositioning.push(null)
        correctionPositioning.push(wordPointer)
        likeness += 2
        queryPointer += 2
        breakInWord = false
      }
      // checks if the query is one off, behind
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer + 1, word, wordPointer + 1)
      ) {
        correctionPositioning.push(null)
        likeness += 2
        queryPointer++
      }
      // Could not find any resemblance so we break the word,
      // meaning that we only check that the query character are in the right place
      else {
        breakInWord = true
      }

      wordPointer++
    }

    return [likeness, correctionPositioning]
  }

  export function calculateLikeness_Without_Positioning(query: string, word: string): [Likeness] {
    let queryPointer = 0
    let wordPointer = 0
    let likeness = 0
    let breakInWord = false

    while (wordPointer < word.length && queryPointer < query.length) {
      // checks if the characters match
      if (WordComparisonAlgorithm.charsMatch(query, queryPointer, word, wordPointer)) {
        likeness += breakInWord ? 1 : MAX_POINT
        queryPointer++
        breakInWord = false
      }
      // checks if the characters are one off, in front
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer, word, wordPointer + 1)
      ) {
        likeness += 2
        queryPointer++
      }
      // checks if the characters are one off, behind
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer, word, wordPointer - 1)
      ) {
        likeness += 2
        queryPointer++
      }
      // checks if the query is one off, in front
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer + 1, word, wordPointer)
      ) {
        likeness += 2
        queryPointer += 2
        breakInWord = false
      }
      // checks if the query is one off, behind
      else if (
        !breakInWord &&
        WordComparisonAlgorithm.charsMatch(query, queryPointer + 1, word, wordPointer + 1)
      ) {
        likeness += 2
        queryPointer++
      }
      // Could not find any resemblance so we break the word,
      // meaning that we only check that the query character are in the right place
      else {
        breakInWord = true
      }

      wordPointer++
    }

    return [likeness]
  }

  /*
   * This function takes a word and the correctionPositioning array and returns a string
   * with '%' in the places where the query characters are not in the right place
   * and the query characters in the right place in the word
   * eg. 'Jaokim Rangel' -> 'J%o%kim %Rangel'
   *
   * This serves the purpose of matching both the query and the corrected word when searching the database
   * This is because we take into account that both the user might be misstyping the word AND that the user
   * HAS typed it right, but it just wasn't in our dictionary. If we query the database with the overlapped pattern
   * and we get back fewer results than weve requested (eg. request a limit of 10 but get 4) we know that we
   * have all the data matching both patterns and we can now safely show the user the appropriate results with
   * the highest likeness score.
   */
  export function overlapSearch(
    word: string,
    correctionPositioning: CorrectionPositioning
  ): string {
    let result = ''

    for (let i = 0; i < correctionPositioning.length; i++) {
      const previous = correctionPositioning[i - 1]
      const current = correctionPositioning[i]
      const next = correctionPositioning[i + 1]

      // If just one letter is typed wrong, e.g 'Joacim' -> 'Joa_im'
      if (
        current === null &&
        typeof previous == 'number' &&
        typeof next == 'number' &&
        previous + 2 == next
      ) {
        result += '_'
      } else if (current === null && next !== null) {
        result += '%'
      } else if (next !== null && current !== null && current > next) {
        result += '%'
        result += word.charAt(next)
        result += '%'
        i++
      } else if (typeof next == 'number' && current !== null && current + 1 != next) {
        result += word.charAt(current)
        result += '%'
      } else if (current !== null) {
        result += word.charAt(current)
      }
    }

    return result
  }

  export function charsMatch(str1: string, index1: number, str2: string, index2: number): boolean {
    return (
      index2 >= 0 &&
      index2 < str2.length &&
      index1 >= 0 &&
      index1 < str1.length &&
      str1.charAt(index1) === str2.charAt(index2)
    )
  }

  export function getPercentageMatch(query: string, points: number): number {
    return points / (query.length * MAX_POINT)
  }
}
